import React, { useState, useEffect, useMemo, useRef } from "react";
import apiClient from "../../../utils/apiClient";
import server from "../../../utils/serverConfig";
import { SearchIcon, X, Info, Inbox, RefreshCw, Upload } from "lucide-react";
import toast from "react-hot-toast";
import { useLtpStore } from "../../../contexts/LTPStore/useLTPStore";
import useStraddleStore from "../../../contexts/LTPStore/useStraddleStore";
import useIndiaStraddleStore from "../../../contexts/LTPStore/useIndiaStraddleStore";
import useIndiaOtmStore from "../../../contexts/LTPStore/useIndiaOtmStore";
import WebSocketManager from "../../../contexts/websocketManager";

// ─────────────────────────────────────────────────────────
// Combined Straddle Row Component
// ─────────────────────────────────────────────────────────
const CombinedStraddleRow = React.memo(({ row, index, isMcx }) => {
    const prevUnder = useRef(row.underlying_ltp);
    const underRef  = useRef(null);

    const flash = (ref) => {
        if (!ref.current) return;
        ref.current.classList.add("price-flash");
        setTimeout(() => ref.current?.classList.remove("price-flash"), 900);
    };

    useEffect(() => {
        if (prevUnder.current !== row.underlying_ltp) {
            flash(underRef);
            prevUnder.current = row.underlying_ltp;
        }
    }, [row.underlying_ltp]);

    const atm = row.atm || {};
    const otm = row.otm || {};

    const atmCostPct = atm.cost_pct;
    const atmPctColor = atmCostPct > 15 ? "text-emerald-400" : atmCostPct >= 8 ? "text-amber-400" : "text-rose-400";

    const otmCostPct = otm.cost_pct;
    const otmPctColor = otmCostPct > 15 ? "text-emerald-400" : otmCostPct >= 8 ? "text-amber-400" : "text-rose-400";

    return (
        <tr className="hover:bg-white/5 transition-colors border-b border-white/5">
            <td className="p-3 text-zinc-400 font-medium text-center">{index + 1}</td>
            <td className="p-3 font-semibold text-emerald-400">
                <div className="flex flex-col">
                    <span>{row.symbol}</span>
                    {isMcx && row.underlying_symbol && (
                        <span className="text-[10px] text-zinc-400 font-mono font-normal leading-tight">{row.underlying_symbol}</span>
                    )}
                </div>
            </td>
            <td ref={underRef} className="p-3 text-right font-medium text-white transition-colors duration-200">
                ₹{row.underlying_ltp != null ? Number(row.underlying_ltp).toFixed(2) : "—"}
            </td>
            <td className="p-3 text-right text-zinc-300">{row.dte != null ? `${row.dte}d` : "—"}</td>

            {/* ATM Columns */}
            <td className="p-3 text-left font-mono text-[10px] whitespace-nowrap bg-white/[0.01]">
                {atm.ce_symbol ? (
                    <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-1">
                            <span className="bg-emerald-500/10 text-emerald-400 px-1 rounded text-[8px] font-bold">C</span>
                            <span className="text-zinc-350">{atm.ce_symbol}</span>
                        </div>
                        <div className="flex items-center gap-1">
                            <span className="bg-rose-500/10 text-rose-400 px-1 rounded text-[8px] font-bold">P</span>
                            <span className="text-zinc-350">{atm.pe_symbol}</span>
                        </div>
                    </div>
                ) : "—"}
            </td>
            <td className="p-3 text-right text-zinc-400 font-mono bg-white/[0.01]">
                <span>{atm.ce_ltp != null ? `₹${Number(atm.ce_ltp).toFixed(2)}` : "—"}</span>
                <span className="text-zinc-600 mx-1">/</span>
                <span>{atm.pe_ltp != null ? `₹${Number(atm.pe_ltp).toFixed(2)}` : "—"}</span>
            </td>
            <td className="p-3 text-right font-bold text-white bg-white/[0.01]">
                {atm.straddle != null ? `₹${Number(atm.straddle).toFixed(2)}` : "—"}
            </td>
            <td className={`p-3 text-right font-extrabold bg-white/[0.01] ${atmPctColor}`}>
                {atmCostPct != null ? `${atmCostPct}%` : "—"}
            </td>
 
            {/* OTM Columns */}
            {!isMcx && (
                <>
                    <td className="p-3 text-left font-mono text-[10px] whitespace-nowrap bg-white/[0.03] border-l border-white/5">
                        {otm.ce_symbol ? (
                            <div className="flex flex-col gap-1">
                                <div className="flex items-center gap-1">
                                    <span className="bg-emerald-500/10 text-emerald-400 px-1 rounded text-[8px] font-bold">C</span>
                                    <span className="text-zinc-350">{otm.ce_symbol}</span>
                                </div>
                                <div className="flex items-center gap-1">
                                    <span className="bg-rose-500/10 text-rose-400 px-1 rounded text-[8px] font-bold">P</span>
                                    <span className="text-zinc-350">{otm.pe_symbol}</span>
                                </div>
                            </div>
                        ) : "—"}
                    </td>
                    <td className="p-3 text-right text-zinc-400 font-mono bg-white/[0.03]">
                        <span>{otm.ce_ltp != null ? `₹${Number(otm.ce_ltp).toFixed(2)}` : "—"}</span>
                        <span className="text-zinc-600 mx-1">/</span>
                        <span>{otm.pe_ltp != null ? `₹${Number(otm.pe_ltp).toFixed(2)}` : "—"}</span>
                    </td>
                    <td className="p-3 text-right font-bold text-white bg-white/[0.03]">
                        {otm.straddle != null ? `₹${Number(otm.straddle).toFixed(2)}` : "—"}
                    </td>
                    <td className={`p-3 text-right font-extrabold bg-white/[0.03] ${otmPctColor}`}>
                        {otmCostPct != null ? `${otmCostPct}%` : "—"}
                    </td>
                </>
            )}
        </tr>
    );
});
CombinedStraddleRow.displayName = "CombinedStraddleRow";

// ─────────────────────────────────────────────────────────
// Main view
// ─────────────────────────────────────────────────────────
const API = server.godseye.baseUrl.replace(/\/$/, "");

const IndiaOptionScreenerView = ({ bucket }) => {
    const isMcx = bucket === "mcx_commodity";

    // ── MCX: server-side real-time snapshot from WS ───────
    const { straddles: mcxSnapshot, lastUpdated: mcxLastUpdated } = useStraddleStore();

    // ── India NSE ATM: server-side real-time snapshot ─────
    const { straddles: indiaSnapshot, lastUpdated: indiaLastUpdated } = useIndiaStraddleStore();

    // ── India NSE OTM: server-side real-time snapshot ─────
    const { straddles: indiaOtmSnapshot, lastUpdated: indiaOtmLastUpdated } = useIndiaOtmStore();

    const [screenerQuery, setScreenerQuery]             = useState("");
    const [screenerSortBy, setScreenerSortBy]           = useState("symbol");
    const [screenerSortDesc, setScreenerSortDesc]       = useState(false);
    const [refreshing, setRefreshing]                   = useState(false);
    const [uploading, setUploading]                     = useState(false);
    const [debouncedScreenerQuery, setDebouncedScreenerQuery] = useState("");

    useEffect(() => {
        const t = setTimeout(() => { setDebouncedScreenerQuery(screenerQuery); }, 400);
        return () => clearTimeout(t);
    }, [screenerQuery]);

    // ── Connect global LTP WS on mount for both MCX & India ──────────────
    useEffect(() => {
        WebSocketManager.getInstance().connectGlobalLtp();
    }, []);

    const triggerRefresh = async () => {
        setRefreshing(true);
        try {
            await apiClient.post(`${API}/alpaca/india-refresh-straddles?max_symbols=500`);
            toast.success("Refresh triggered! Loading shortly.");
        } catch (error) {
            toast.error("Failed to trigger refresh");
        } finally {
            setRefreshing(false);
        }
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const formData = new FormData();
        formData.append("file", file);

        setUploading(true);
        try {
            const res = await apiClient.post(`${API}/alpaca/upload-otm-list`, formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            });
            if (res.data?.status === "success") {
                toast.success(res.data.message || "OTM list uploaded successfully!");
            } else {
                toast.error("Failed to upload OTM list");
            }
        } catch (error) {
            console.error("Failed to upload OTM list:", error);
            toast.error(error.response?.data?.detail || "Failed to upload OTM list");
        } finally {
            setUploading(false);
            e.target.value = ""; 
        }
    };

    useEffect(() => {
        setScreenerSortBy("symbol");
        setScreenerSortDesc(false);
    }, [bucket]);

    const handleSort = (col) => {
        if (screenerSortBy === col) setScreenerSortDesc(!screenerSortDesc);
        else { setScreenerSortBy(col); setScreenerSortDesc(true); }
    };

    // Combine, search, filter, and sort ATM and OTM streams into a single list
    const sortedCombinedRows = useMemo(() => {
        const atmMap = new Map((isMcx ? mcxSnapshot : indiaSnapshot).map(r => [r.symbol, r]));
        const otmMap = new Map((isMcx ? [] : indiaOtmSnapshot).map(r => [r.symbol, r]));
        const allSymbols = Array.from(new Set([...atmMap.keys(), ...otmMap.keys()]));

        let combined = allSymbols.map(sym => {
            const atmRow = atmMap.get(sym);
            const otmRow = otmMap.get(sym);
            
            const underlying_ltp = atmRow?.underlying_ltp ?? otmRow?.underlying_ltp ?? atmRow?.underlying_price ?? otmRow?.underlying_price;
            const underlying_symbol = atmRow?.underlying_symbol ?? otmRow?.underlying_symbol;
            const dte = atmRow?.dte ?? otmRow?.dte;
            
            return {
                symbol: sym,
                underlying_symbol,
                underlying_ltp,
                dte,
                atm: atmRow,
                otm: otmRow
            };
        });

        // Filter search
        if (debouncedScreenerQuery) {
            const q = debouncedScreenerQuery.toLowerCase();
            combined = combined.filter(r =>
                r.symbol?.toLowerCase().includes(q) ||
                r.underlying_symbol?.toLowerCase().includes(q) ||
                r.atm?.ce_symbol?.toLowerCase().includes(q) ||
                r.atm?.pe_symbol?.toLowerCase().includes(q) ||
                r.otm?.ce_symbol?.toLowerCase().includes(q) ||
                r.otm?.pe_symbol?.toLowerCase().includes(q)
            );
        }

        // Apply Sorting
        if (screenerSortBy) {
            combined.sort((a, b) => {
                let valA, valB;

                if (screenerSortBy === "symbol") {
                    valA = a.symbol;
                    valB = b.symbol;
                } else if (screenerSortBy === "underlying_ltp" || screenerSortBy === "underlying_price") {
                    valA = a.underlying_ltp;
                    valB = b.underlying_ltp;
                } else if (screenerSortBy === "dte") {
                    valA = a.dte;
                    valB = b.dte;
                } else if (screenerSortBy === "straddle") {
                    valA = a.atm?.straddle;
                    valB = b.atm?.straddle;
                } else if (screenerSortBy === "cost_pct") {
                    valA = a.atm?.cost_pct;
                    valB = b.atm?.cost_pct;
                } else if (screenerSortBy === "otm_straddle") {
                    valA = a.otm?.straddle;
                    valB = b.otm?.straddle;
                } else if (screenerSortBy === "otm_cost_pct") {
                    valA = a.otm?.cost_pct;
                    valB = b.otm?.cost_pct;
                } else {
                    valA = a.symbol;
                    valB = b.symbol;
                }

                if (valA == null) return screenerSortDesc ? 1 : -1;
                if (valB == null) return screenerSortDesc ? -1 : 1;

                if (typeof valA === "string") {
                    valA = valA.toLowerCase();
                    valB = (valB || "").toLowerCase();
                }

                if (valA < valB) return screenerSortDesc ? 1 : -1;
                if (valA > valB) return screenerSortDesc ? -1 : 1;
                return 0;
            });
        }

        return combined;
    }, [isMcx, mcxSnapshot, indiaSnapshot, indiaOtmSnapshot, debouncedScreenerQuery, screenerSortBy, screenerSortDesc]);

    const useWsRows = isMcx || indiaSnapshot.length > 0;
    const hasNoData = sortedCombinedRows.length === 0;

    const currentSnapshot = isMcx ? mcxSnapshot : indiaSnapshot;
    const currentLastUpdated = isMcx ? mcxLastUpdated : indiaLastUpdated;

    return (
        <div className="flex-1 flex flex-col min-h-0 bg-[#14233a] border border-[#1a2b4c] rounded-xl p-4 shadow-lg text-white">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-4 border-b border-white/5 pb-3">
                <div className="flex items-center gap-4 flex-wrap">
                    <span className="text-xs font-bold uppercase tracking-wider text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-3 py-1.5 rounded-md select-none">
                        {isMcx ? "MCX Commodities" : "India Stocks"} Options Screener
                    </span>

                    {currentSnapshot.length > 0 && (
                        <span className="flex items-center gap-1.5 text-[10px] font-bold px-2.5 py-1 rounded-md border border-emerald-400/20 text-emerald-400 bg-emerald-400/10 animate-pulse select-none">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
                            LIVE REALTIME
                        </span>
                    )}

                    {currentLastUpdated && (
                        <span className="text-[10px] text-zinc-500 font-mono">
                            Last tick (ATM): {new Date(currentLastUpdated).toLocaleTimeString()}
                        </span>
                    )}
                    {!isMcx && indiaOtmLastUpdated && (
                        <span className="text-[10px] text-zinc-500 font-mono border-l border-white/10 pl-2">
                            Last tick (OTM): {new Date(indiaOtmLastUpdated).toLocaleTimeString()}
                        </span>
                    )}
                </div>

                <div className="flex items-center gap-3">
                    <div className="relative">
                        <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                        <input type="text"
                            placeholder={isMcx ? "Search MCX symbol…" : "Search Indian Stock symbol…"}
                            value={screenerQuery}
                            onChange={(e) => setScreenerQuery(e.target.value)}
                            className="bg-[#22272b] border border-transparent focus:border-zinc-650 rounded-lg pl-9 pr-8 py-1.5 text-xs text-[#b6c2cf] focus:outline-none transition-all placeholder:text-zinc-500 w-64 shadow-inner"
                        />
                        {screenerQuery && (
                            <button onClick={() => setScreenerQuery("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white">
                                <X className="w-3.5 h-3.5" />
                            </button>
                        )}
                    </div>

                    {!isMcx && (
                        <div className="flex items-center gap-2">
                            <label className={`flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 border border-emerald-500/20 rounded-lg text-xs font-bold text-white transition-all cursor-pointer select-none ${uploading ? "opacity-50 cursor-not-allowed" : ""}`}>
                                <Upload className={`w-3.5 h-3.5 ${uploading ? "animate-pulse" : ""}`} />
                                {uploading ? "Uploading…" : "Upload OTM Excel"}
                                <input
                                    type="file"
                                    accept=".xlsx,.xls"
                                    onChange={handleFileUpload}
                                    disabled={uploading}
                                    className="hidden"
                                />
                            </label>

                            <button onClick={triggerRefresh} disabled={refreshing}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg text-xs text-zinc-300 hover:text-white transition-all disabled:opacity-50">
                                <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
                                {refreshing ? "Refreshing…" : "Refresh"}
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Unified Options Table Grid */}
            <div className="flex-1 flex flex-col min-h-0 overflow-hidden pr-1">
                {hasNoData ? (
                    <div className="flex-1 flex flex-col items-center justify-center py-20 text-zinc-500">
                        {useWsRows ? (
                            <>
                                <div className="w-10 h-10 border-2 border-zinc-700/30 border-t-zinc-500 rounded-full animate-spin mb-4"></div>
                                <p className="text-sm font-semibold text-zinc-400">Waiting for live data streaming…</p>
                            </>
                        ) : (
                            <>
                                <Inbox className="w-12 h-12 text-zinc-700 mb-3" />
                                <p className="text-sm font-semibold">No Option Data Available</p>
                                <p className="text-xs text-zinc-650 mt-1">Run a refresh or upload daily Excel to stream values.</p>
                            </>
                        )}
                    </div>
                ) : (
                    <div className="flex-1 overflow-auto rounded-lg border border-white/5 bg-[#0d0f18]/65 custom-scroll">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                {/* Grouped Header */}
                                <tr className="bg-[#1a2b4c]/60 text-zinc-350 text-xs font-bold select-none border-b border-white/10 text-center">
                                    <th className="p-3 border-r border-white/5" rowSpan={2}>#</th>
                                    <th className="p-3 border-r border-white/5" colSpan={3}>Asset Underlying Info</th>
                                    <th className="p-3 bg-white/[0.01] border-r border-white/5" colSpan={4}>ATM Straddle Options (Live ATM)</th>
                                    {!isMcx && (
                                        <th className="p-3 bg-white/[0.03]" colSpan={4}>OTM Strangle Options (Daily Excel Upload)</th>
                                    )}
                                </tr>
                                <tr className="bg-[#14233a] border-b border-white/10 text-zinc-400 text-[11px] font-semibold select-none">
                                    <th className="p-3 hover:text-white cursor-pointer" onClick={() => handleSort("symbol")}>
                                        Symbol {screenerSortBy === "symbol" && (screenerSortDesc ? " ↓" : " ↑")}
                                    </th>
                                    <th className="p-3 text-right hover:text-white cursor-pointer" onClick={() => handleSort(isMcx ? "underlying_ltp" : "underlying_price")}>
                                        Price {screenerSortBy === (isMcx ? "underlying_ltp" : "underlying_price") && (screenerSortDesc ? " ↓" : " ↑")}
                                    </th>
                                    <th className="p-3 text-right hover:text-white cursor-pointer" onClick={() => handleSort("dte")}>
                                        DTE {screenerSortBy === "dte" && (screenerSortDesc ? " ↓" : " ↑")}
                                    </th>

                                    {/* ATM headers */}
                                    <th className="p-3 bg-white/[0.01]">CE / PE Symbol</th>
                                    <th className="p-3 text-right bg-white/[0.01]">Call / Put LTP</th>
                                    <th className="p-3 text-right bg-white/[0.01] hover:text-white cursor-pointer" onClick={() => handleSort("straddle")}>
                                        Straddle Cost {screenerSortBy === "straddle" && (screenerSortDesc ? " ↓" : " ↑")}
                                    </th>
                                    <th className="p-3 text-right bg-white/[0.01] hover:text-white cursor-pointer" onClick={() => handleSort("cost_pct")}>
                                        Cost % {screenerSortBy === "cost_pct" && (screenerSortDesc ? " ↓" : " ↑")}
                                    </th>

                                    {/* OTM headers */}
                                    {!isMcx && (
                                        <>
                                            <th className="p-3 bg-white/[0.03] border-l border-white/5">CE / PE Symbol</th>
                                            <th className="p-3 text-right bg-white/[0.03]">Call / Put LTP</th>
                                            <th className="p-3 text-right bg-white/[0.03] hover:text-white cursor-pointer select-none" onClick={() => handleSort("otm_straddle")}>
                                                Strangle Cost {screenerSortBy === "otm_straddle" && (screenerSortDesc ? " ↓" : " ↑")}
                                            </th>
                                            <th className="p-3 text-right bg-white/[0.03] hover:text-white cursor-pointer select-none" onClick={() => handleSort("otm_cost_pct")}>
                                                Cost % {screenerSortBy === "otm_cost_pct" && (screenerSortDesc ? " ↓" : " ↑")}
                                            </th>
                                        </>
                                    )}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-white/5 text-xs text-zinc-300">
                                {sortedCombinedRows.map((row, i) => (
                                    <CombinedStraddleRow key={row.symbol} row={row} index={i} isMcx={isMcx} />
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default IndiaOptionScreenerView;
